import { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { TIPO_SECAO_PAGINA_LABEL } from '../../../../model/apm/apm-enums';
import { ICustomPageSectionResponse } from '../../../../model/apm/custom-page/response/custom-page-section-response';
import { ICustomPageSectionItemResponse } from '../../../../model/apm/custom-page/response/custom-page-section-item-response';
import { ConfiguredFields } from '../../components/configured-fields';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { validateFields } from '../../components/field-config';
import { CUSTOM_PAGE_SECTION_CONFIG } from './custom-page-section-config';
import { CUSTOM_PAGE_SECTION_ICONS } from './custom-page-section-icons';

interface CustomPageSectionItemDrawerProps {
    open: boolean;
    section?: ICustomPageSectionResponse;
    item?: ICustomPageSectionItemResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: Record<string, any>) => void;
}

export function CustomPageSectionItemDrawer({ open, section, item, isSaving, onClose, onSave }: CustomPageSectionItemDrawerProps) {
    const [form, setForm] = useState<Record<string, any>>({});
    const [showErrors, setShowErrors] = useState(false);
    const [uploading, setUploading] = useState(false);
    const isEdit = !!item;
    const config = section ? CUSTOM_PAGE_SECTION_CONFIG[section.type] : undefined;
    const fields = useMemo(() => config?.itemFields || {}, [config]);
    const noun = (config?.itemNoun || 'Item').toLowerCase();
    const errors = useMemo(() => validateFields(fields, form), [fields, form]);

    useEffect(() => {
        if (!open) return;
        setForm(item ? { ...item } : {});
        setShowErrors(false);
        setUploading(false);
    }, [open, item]);

    function handleSave() {
        if (Object.keys(errors).length > 0) {
            setShowErrors(true);
            return;
        }
        onSave(form);
    }

    const sectionName = section ? (section.title || TIPO_SECAO_PAGINA_LABEL[section.type]) : '';

    return (
        <FormDrawer
            open={open}
            icon={section ? CUSTOM_PAGE_SECTION_ICONS[section.type] : <ListAltIcon />}
            title={isEdit ? `Editar ${noun}` : `Adicionar ${noun}`}
            subtitle={`Seção: ${sectionName}`}
            saveLabel={isEdit ? 'Salvar alterações' : `Adicionar ${noun}`}
            isSaving={isSaving}
            saveDisabled={uploading}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Conteúdo" description={config?.description}>
                <Grid container spacing={2}>
                    <ConfiguredFields
                        idPrefix="custom-page-item"
                        fields={fields}
                        value={form}
                        errors={errors}
                        showErrors={showErrors}
                        onChange={(patch) => setForm((current) => ({ ...current, ...patch }))}
                        onUploadingChange={setUploading}
                    />
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
